import { useState, useEffect } from 'react'
import AdminLogin from '../components/AdminLogin'
import AdminLayout from '../components/AdminLayout'
import PageMeta from '../components/PageMeta'

const API_BASE_URL =
  import.meta.env.VITE_HARBOR_OPS_API_URL || "http://127.0.0.1:8000"

interface Lead {
  first_name?: string
  last_name?: string
  company?: string
  title_or_role?: string
  linkedin_url?: string
  email?: string
  company_url?: string
  icp_score_100?: number
  icp_explanation?: string
  role_archetype?: string
  company_type?: string
  notes_context?: string
  source_tag?: string
  pipeline_stage?: 'prospect' | 'engaged' | 'qualified' | 'proposal_sent' | 'purchased' | 'evangelist'
  outreach_status?: 'not_contacted' | 'dm_1_sent' | 'dm_2_bump_sent' | 'email_1_sent' | 'email_2_sent' | 'email_3_sent' | 'replied' | 'not_interested' | 'purchased'
  contact_channel_primary?: 'linkedin_dm' | 'email' | 'both'
  last_contact_channel?: string
  last_contacted_at?: string
  last_message_variant?: string
  last_message_notes?: string
  next_step?: string
  next_step_due_date?: string
}

interface Sale {
  sessionId: string
  packSlug: string
  customerEmail: string
  customerName?: string
  organization?: string
  amountTotal: number
  currency: string
  purchasedAt: string
}

export default function CRMPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [leads, setLeads] = useState<Lead[]>([])
  const [sales, setSales] = useState<Sale[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'leads' | 'sales' | 'outreach' | 'pipeline'>('leads')

  const fetchLeads = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/revenue/leads`)
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const data = await response.json()
      setLeads(data.leads || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch leads')
    }
  }

  const fetchSales = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/revenue/sales`)
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const data = await response.json()
      setSales(data.sales || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch sales')
    }
  }

  useEffect(() => {
    if (isAuthenticated) {
      setIsLoading(true)
      Promise.all([fetchLeads(), fetchSales()]).finally(() => setIsLoading(false))
    }
  }, [isAuthenticated])

  const getPipelineStageColor = (stage?: string) => {
    switch (stage) {
      case 'purchased': return 'bg-green-100 text-green-800'
      case 'evangelist': return 'bg-purple-100 text-purple-800'
      case 'proposal_sent': return 'bg-blue-100 text-blue-800'
      case 'qualified': return 'bg-yellow-100 text-yellow-800'
      case 'engaged': return 'bg-orange-100 text-orange-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getOutreachStatusColor = (status?: string) => {
    switch (status) {
      case 'purchased': return 'bg-green-100 text-green-800'
      case 'replied': return 'bg-blue-100 text-blue-800'
      case 'not_interested': return 'bg-red-100 text-red-800'
      case 'email_3_sent': return 'bg-yellow-100 text-yellow-800'
      case 'email_2_sent': return 'bg-orange-100 text-orange-800'
      case 'email_1_sent': case 'dm_2_bump_sent': return 'bg-gray-100 text-gray-800'
      case 'dm_1_sent': return 'bg-gray-50 text-gray-600'
      default: return 'bg-gray-50 text-gray-500'
    }
  }

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—'
    try {
      return new Date(dateStr).toLocaleDateString()
    } catch {
      return dateStr
    }
  }

  const formatPrice = (cents: number) => {
    return `$${(cents / 100).toFixed(2)}`
  }

  if (!isAuthenticated) {
    return <AdminLogin onAuthSuccess={() => setIsAuthenticated(true)} />
  }

  return (
    <>
      <PageMeta
        title="CRM - Harbor Agent Admin"
        description="Manage leads, sales, and outreach pipeline"
      />
      <AdminLayout>
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">CRM Dashboard</h2>
            <p className="text-gray-600">
              Track leads, outreach, and sales pipeline
            </p>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="flex space-x-8">
              {[
                { id: 'leads', label: 'Leads', count: leads.length },
                { id: 'sales', label: 'Sales', count: sales.length },
                { id: 'outreach', label: 'Outreach', count: leads.filter(l => l.outreach_status && l.outreach_status !== 'not_contacted').length },
                { id: 'pipeline', label: 'Pipeline', count: leads.filter(l => l.pipeline_stage && !['purchased', 'evangelist'].includes(l.pipeline_stage)).length },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                  {tab.count > 0 && (
                    <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                      activeTab === tab.id ? 'bg-primary-100 text-primary-800' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-800 rounded-lg">
              {error}
            </div>
          )}

          {isLoading ? (
            <div className="text-center py-12">
              <div className="text-gray-600">Loading CRM data...</div>
            </div>
          ) : (
            <>
              {/* Leads Tab */}
              {activeTab === 'leads' && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-gray-900">All Leads</h3>
                    <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
                      Add Lead
                    </button>
                  </div>
                  {leads.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                      <p className="text-gray-600">No leads found</p>
                      <p className="text-sm text-gray-500 mt-2">Import leads from LinkedIn research or add manually</p>
                    </div>
                  ) : (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ICP Score</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pipeline</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Outreach</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Contact</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {leads.map((lead, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  {lead.first_name} {lead.last_name}
                                </div>
                                <div className="text-sm text-gray-500">{lead.email}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">{lead.company || '—'}</div>
                                <div className="text-sm text-gray-500">{lead.title_or_role || '—'}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {lead.icp_score_100 !== undefined ? (
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    lead.icp_score_100 >= 70 ? 'bg-green-100 text-green-800' :
                                    lead.icp_score_100 >= 50 ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {lead.icp_score_100}/100
                                  </span>
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {lead.pipeline_stage ? (
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPipelineStageColor(lead.pipeline_stage)}`}>
                                    {lead.pipeline_stage.replace('_', ' ')}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {lead.outreach_status ? (
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getOutreachStatusColor(lead.outreach_status)}`}>
                                    {lead.outreach_status.replace('_', ' ')}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">not contacted</span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatDate(lead.last_contacted_at)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                <button
                                  onClick={() => {
                                    // TODO: Open edit modal
                                    alert('Edit functionality coming soon')
                                  }}
                                  className="text-blue-600 hover:text-blue-800"
                                >
                                  Edit
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Sales Tab */}
              {activeTab === 'sales' && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-gray-900">Sales Records</h3>
                    <div className="text-sm text-gray-600">
                      Total: {formatPrice(sales.reduce((sum, s) => sum + s.amountTotal, 0))}
                    </div>
                  </div>
                  {sales.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                      <p className="text-gray-600">No sales recorded yet</p>
                      <p className="text-sm text-gray-500 mt-2">Sales from Stripe will appear here automatically</p>
                    </div>
                  ) : (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pack</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Session ID</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {sales.map((sale) => (
                            <tr key={sale.sessionId} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {formatDate(sale.purchasedAt)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  {sale.customerName || sale.customerEmail}
                                </div>
                                <div className="text-sm text-gray-500">{sale.organization || '—'}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  {sale.packSlug}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {formatPrice(sale.amountTotal)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                                {sale.sessionId.substring(0, 20)}...
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Outreach Tab */}
              {activeTab === 'outreach' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Outreach Campaigns</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="text-sm text-gray-600">LinkedIn DMs Sent</div>
                      <div className="text-2xl font-bold text-gray-900">
                        {leads.filter(l => l.outreach_status?.startsWith('dm_')).length}
                      </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="text-sm text-gray-600">Emails Sent</div>
                      <div className="text-2xl font-bold text-gray-900">
                        {leads.filter(l => l.outreach_status?.startsWith('email_')).length}
                      </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="text-sm text-gray-600">Replies</div>
                      <div className="text-2xl font-bold text-green-600">
                        {leads.filter(l => l.outreach_status === 'replied').length}
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <p className="text-sm text-gray-600">
                      Outreach tracking integrates with your LinkedIn DM and email campaigns.
                      Update lead status after each outreach to track progress.
                    </p>
                    <div className="mt-4 space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-gray-300 rounded-full"></span>
                        <span>LinkedIn DM campaigns: Track via <code className="bg-gray-100 px-1 rounded">outreach_status</code> field</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-gray-300 rounded-full"></span>
                        <span>Email campaigns: Track via <code className="bg-gray-100 px-1 rounded">outreach_status</code> field</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-gray-300 rounded-full"></span>
                        <span>Click-through tracking: Coming soon</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Pipeline Tab */}
              {activeTab === 'pipeline' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Sales Pipeline</h3>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                    {['prospect', 'engaged', 'qualified', 'proposal_sent', 'purchased'].map((stage) => {
                      const count = leads.filter(l => l.pipeline_stage === stage).length
                      return (
                        <div key={stage} className="bg-white p-4 rounded-lg border border-gray-200">
                          <div className="text-sm text-gray-600 capitalize">{stage.replace('_', ' ')}</div>
                          <div className="text-2xl font-bold text-gray-900">{count}</div>
                        </div>
                      )
                    })}
                  </div>
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Pipeline Stages</h4>
                    <div className="space-y-2 text-sm text-gray-700">
                      <div><strong>Prospect:</strong> Initial lead identified</div>
                      <div><strong>Engaged:</strong> Lead has responded or shown interest</div>
                      <div><strong>Qualified:</strong> Lead meets ICP criteria and has budget</div>
                      <div><strong>Proposal Sent:</strong> Pack information or proposal shared</div>
                      <div><strong>Purchased:</strong> Sale completed (automatically updated from Stripe)</div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </AdminLayout>
    </>
  )
}

